import React, { useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";

type FlowCard = {
  id: "pas" | "pas_samtykke" | "koerekort" | "koerekort_samtykke" | "tidsbestilling";
  title: string;
  description: string;
  path: string;
  available: boolean;
};

const PROFILE_FLOWS: Record<string, Array<FlowCard["id"]>> = {
  partnerA: ["pas", "pas_samtykke", "koerekort_samtykke"],
  partnerB: ["pas"],
  kommuneX: ["pas", "tidsbestilling"]
};

const ALL: Array<FlowCard["id"]> = ["pas", "pas_samtykke", "koerekort", "koerekort_samtykke", "tidsbestilling"];

const Portal: React.FC = () => {
  const { profileId } = useParams();
  const location = useLocation();
  const qp = new URLSearchParams(location.search);
  const flowsParam = qp.get("flows");

  const allowedIds = useMemo(() => {
    if (flowsParam) {
      const rawIds = flowsParam.split(",").map(s => s.trim()).filter(Boolean);
      const normalized = rawIds.map(id => {
        if (id === "koerekort_parent_consent") {return "koerekort_samtykke";}
        if (id === "samtykke") {return "pas_samtykke";}
        return id;
      });
      return normalized.filter((id): id is FlowCard["id"] => (ALL as string[]).includes(id));
    }
    if (profileId && PROFILE_FLOWS[profileId]) {return PROFILE_FLOWS[profileId];}
    return ALL;
  }, [flowsParam, profileId]);

  const cards: FlowCard[] = [
    {
      id: "pas",
      title: "Pas",
      description: "Ansøg om nyt pas og vælg tid.",
      path: "/pas",
      available: allowedIds.includes("pas")
    },
    {
      id: "pas_samtykke",
      title: "Pas samtykke",
      description: "Afgiv samtykke digitalt.",
      path: "/pas_samtykke",
      available: allowedIds.includes("pas_samtykke")
    },
    {
      id: "koerekort",
      title: "Kørekort",
      description: "Ansøg om kørekort (kommer snart).",
      path: "/koerekort",
      available: allowedIds.includes("koerekort")
    },
    {
      id: "koerekort_samtykke",
      title: "Kørekort samtykke (P23T)",
      description: "Digital samtykkeerklæring til kørekort for 17-årige (demo).",
      path: "/koerekort_samtykke",
      available: allowedIds.includes("koerekort_samtykke")
    },
    {
      id: "tidsbestilling",
      title: "Tidsbestilling",
      description: "Bestil en tid (kommer snart).",
      path: "/tidsbestilling",
      available: allowedIds.includes("tidsbestilling")
    }
  ];

  const visible = cards.filter(c => c.available);

  return (
    <div className="step-content">
      <h1>Portal</h1>
      <p>Vælg en selvbetjeningsløsning herunder.</p>
      <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))" }}>
        {visible.map(card => (
          <div key={card.id} className="info-box" data-testid={`portal-card-${card.id}`}>
            <h2 style={{ marginTop: 0 }}>{card.title}</h2>
            <p>{card.description}</p>
            <Link className="btn btn-primary" to={card.path}>Gå til {card.title}</Link>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="info-box">
            <p>Der er ingen tjenester tilgængelige via dette link.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portal;


